import { useCallback, useRef, useState } from 'react';
import type { ZodTypeAny, TypeOf } from 'zod';
import { RequestError } from './api.js';

/**
 * Minimal form state helper.
 *
 * Validation runs against the same Zod schemas the API uses, so a field is
 * rejected by identical rules on both sides. Errors are stored as i18n keys and
 * translated at render time.
 */
export function useForm<S extends ZodTypeAny>(schema: S, initial: Record<string, unknown>) {
  const [values, setValues] = useState<Record<string, unknown>>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setValue = useCallback((name: string, value: unknown) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const { [name]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  /**
   * Every call site writes `initial` as an object literal, so it is a new object
   * on every render. A `reset` that depended on it changed identity just as
   * often — and both callers that hold one list it in an effect dependency
   * array, so those effects re-ran after every single render. The quote editor
   * reset its fields on each keystroke, making a saved draft impossible to edit;
   * the reminder dialog wiped validation errors the instant they appeared,
   * leaving a red box with no message. The ref pins the first `initial`, which is
   * what "reset to the initial values" means anyway.
   */
  const initialRef = useRef(initial);
  const reset = useCallback((next: Record<string, unknown> = initialRef.current) => {
    setValues(next);
    setErrors({});
    setFormError(null);
  }, []);

  /**
   * `compose` lets a screen validate the payload it actually sends rather than
   * raw field state — needed wherever part of the form lives outside `values`
   * (quote line items) or a field is only meaningful in one mode (customerId is
   * absent when creating an inline customer). Without it the schema would run
   * against values the request never carries.
   */
  const submit = useCallback(
    async (
      onValid: (data: TypeOf<S>) => Promise<void> | void,
      compose?: (values: Record<string, unknown>) => unknown,
    ) => {
      setFormError(null);

      const parsed = schema.safeParse(compose ? compose(values) : values);
      if (!parsed.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path.join('.') || '_';
          if (!fieldErrors[key]) fieldErrors[key] = issue.message;
        }
        setErrors(fieldErrors);
        setFormError('errors.validation');
        return;
      }

      setSubmitting(true);
      try {
        await onValid(parsed.data as TypeOf<S>);
      } catch (error) {
        if (error instanceof RequestError) {
          // The server may reject fields the client schema accepts (uniqueness,
          // state conflicts) — surface those on the same fields.
          setErrors(error.fields);
          setFormError(error.code);
        } else {
          setFormError('errors.unknown');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [schema, values],
  );

  return { values, errors, formError, submitting, setValue, setValues, reset, submit, setFormError };
}
